import { Collapse } from '@mui/material';
import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { Translation } from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useOnboardingContext } from '@swf/components/Onboarding/OnboardingContext/OnbordingContext';
import Arrow from '@swf/core/assets/icons/arrow.svg';
import WarningTriangleIcon from '@swf/core/assets/icons/warning-triangle.svg';
import WarningCircleIcon from '@swf/core/assets/icons/warning.svg';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { Alert, CashierLimitTypes, DepositLimitStatus, Separator } from '@swf/first';

import { useCashierWidget } from '../../model';
import { CashierOnboardingStep } from '../WalletHomeContainer/cashierOnboardingStepsInfo';

import {
  TXTWCL_Deposits_are_currently_blocked,
  TXTWCL_Reapply_regulatory_limit,
  TXTWCL_Set_your_self_imposed_limit,
  TXTWCL_You_have_chosen_to_block_depos_ELL,
  TXTWCL_You_have_reapplied_the_regulat_ELL
} from './WalletDepositLimitsPanel.messages.json';
import {
  active,
  activeStepTour,
  arrow,
  header,
  headerContent,
  limitsLink,
  separator,
  warningCircle,
  warningTriangle,
  wrapper
} from './WalletDepositLimitsPanel.scss';
import CollapseButton from './components/CollapseButton/CollapseButton';
import WalletDepositLimitsRecord from './components/WalletDepositLimitsRecord/WalletDepositLimitsRecord';

const WalletDepositLimitsPanel: React.FC = () => {
  const { widgetSelect } = useCashierWidget();
  const closestLimits = useSelector(widgetSelect.getClosestLimit);
  const { selfImposedLimit, regulatoryLimit, overallClosest } = closestLimits;

  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const notSetLimits = closestLimits.overallClosest?.limitStatus === DepositLimitStatus.NotSet;
  const shouldDisplayLimitsWidget = !(notSetLimits && isSelfExcluded);

  const [expand, setExpand] = useState(false);
  const [showReapplyAlert, setShowReapplyAlert] = useState(false);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const { setFlowToStep, activeStep, onboardingFinished } = useOnboardingContext();

  const depositLimitPath = useSelector(select.account.getDepositLimitPath);
  const license = useSelector(select.account.getLicense);
  const { enqueue } = useDispatch<Dispatch>().modal;

  const openRestrictDepositLimit = () => {
    isEmbedded
      ? window.starsweb?.app.execute('menu/requests/responsible-gaming/deposit-limit')
      : enqueue({ path: depositLimitPath });
  };

  useEffect(() => {
    if (selfImposedLimit || regulatoryLimit) {
      setExpand(true);
    }
  }, [selfImposedLimit, regulatoryLimit]);

  const setIcon = () => {
    if (!overallClosest) {
      return;
    }
    if (overallClosest.progress >= 100) {
      return <WarningCircleIcon className={warningCircle} />;
    }
    if (overallClosest.progress >= 80) {
      return <WarningTriangleIcon className={warningTriangle} />;
    }
  };

  return (
    <>
      {shouldDisplayLimitsWidget && (
        <div
          className={classNames(wrapper, {
            [activeStepTour]:
              !onboardingFinished && activeStep === CashierOnboardingStep.StepLimitPanel
          })}
          data-testid="wallet-deposit-limits"
          ref={
            !onboardingFinished && setFlowToStep
              ? setFlowToStep(CashierOnboardingStep.StepLimitPanel)
              : null
          }
        >
          {closestLimits.overallClosest?.limitStatus === DepositLimitStatus.SelfExcluded ? (
            <>
              <div className={header} onClick={() => setExpand(!expand)}>
                <Translation {...TXTWCL_Deposits_are_currently_blocked} />
                <Arrow className={classNames(arrow, { [active]: expand })} />
              </div>
              <Collapse in={expand}>
                <div className={headerContent}>
                  <Translation
                    {...TXTWCL_You_have_chosen_to_block_depos_ELL}
                    values={{
                      a: (msg: React.ReactNode) => (
                        <StarsLink onClick={openRestrictDepositLimit}>{msg}</StarsLink>
                      )
                    }}
                  />
                </div>
              </Collapse>
            </>
          ) : (
            <>
              {(selfImposedLimit || regulatoryLimit) && (
                <Collapse in={expand}>
                  <CollapseButton startIcon={setIcon()}>
                    {selfImposedLimit && (
                      <WalletDepositLimitsRecord limitType={CashierLimitTypes.SelfImposedLimit} />
                    )}

                    {showReapplyAlert && expand && (
                      <Alert variant="success">
                        <Translation {...TXTWCL_You_have_reapplied_the_regulat_ELL} />
                      </Alert>
                    )}

                    {license === LicenceId.BELGIUM && regulatoryLimit && (
                      <WalletDepositLimitsRecord limitType={CashierLimitTypes.RegulatoryLimit} />
                    )}
                    {!selfImposedLimit && regulatoryLimit && (
                      <WalletDepositLimitsRecord limitType={CashierLimitTypes.SelfImposedLimit} />
                    )}
                  </CollapseButton>
                </Collapse>
              )}

              {!selfImposedLimit && (
                <StarsLink
                  className={limitsLink}
                  onClick={() => openRestrictDepositLimit()}
                  data-testid="self-imposed-link"
                >
                  <Translation {...TXTWCL_Set_your_self_imposed_limit} />
                </StarsLink>
              )}

              {license === LicenceId.BELGIUM && !regulatoryLimit && (
                <>
                  {selfImposedLimit && (
                    <div className={separator}>
                      <Separator />
                    </div>
                  )}
                  <StarsLink
                    onClick={() => {
                      if (!expand) {
                        setExpand(true);
                      }
                      setShowReapplyAlert(true);
                    }}
                    data-testid="regulatory-link"
                    className={limitsLink}
                  >
                    <Translation {...TXTWCL_Reapply_regulatory_limit} />
                  </StarsLink>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default WalletDepositLimitsPanel;
